import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';

export default function IntroVideoSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-8 md:py-12 bg-background-100/50 overflow-hidden"
    >
      <FloatingShapes count={14} color="accent" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={10} color="secondary" speed={0.5} />
      </div>
      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Heading */}
          <div
            className={`text-center mb-6 md:mb-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-secondary-100/80 rounded-full w-fit mx-auto mb-5">
              <span className="w-1.5 h-1.5 bg-secondary-500 rounded-full animate-pulse" />
              <span className="text-xs font-mono font-medium text-secondary-700 uppercase tracking-wider">
                {t('home.intro.badge')}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-foreground-950 tracking-tight mb-4">
              {t('home.intro.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {t('home.intro.subtitle')}
            </p>
          </div>

          {/* Video */}
          <div
            className={`transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative rounded-lg overflow-hidden border border-background-200/60 bg-background-50">
              {/* Glow behind video */}
              <div className="absolute inset-0 -m-6 rounded-3xl bg-accent-500/5 blur-2xl pointer-events-none" />
              <video
                className="relative w-full aspect-video object-cover"
                controls
                playsInline
                preload="metadata"
                poster="https://readdy.ai/api/search-image?query=Central%20glowing%20geometric%20core%20surrounded%20by%20flowing%20luminous%20data%20streams%2C%20deep%20violet%20purple%20and%20soft%20emerald%20mint%20gradient%20background%2C%20clean%20minimal%20futuristic%20enterprise%20aesthetic%2C%20soft%20depth%20of%20field%2C%20cinematic%20still%20frame&width=1600&height=900&seq=intro-video-poster-02&orientation=landscape&nocache=true"
              >
                <source
                  key={isEnglish ? 'en' : 'es'}
                  src={
                    isEnglish
                      ? 'https://storage.helloreaddy.io/project_files/bb209c14-9fa9-4034-a099-af83f1204fe6/011b580b-136e-4f72-9386-be811a28d54d_video-2-ing.mp4'
                      : 'https://storage.helloreaddy.io/project_files/bb209c14-9fa9-4034-a099-af83f1204fe6/6e1e0a72-8ace-4a36-90f8-959df6a31993_WhatsApp-Video-2026-08-24-at-10.43.59-PM.mp4'
                  }
                  type="video/mp4"
                />
              </video>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}